import { baseApi } from "@/store/baseApi";

/**
 * Auto Order API slice — calls the modern users service directly
 * (`users/subscriptions...`, `users/popular-products`). PI-1372.
 *
 * **Architectural notes** (read these before editing):
 * - Endpoints hit the users service directly (Apache proxies `/rest/users/*`
 *   straight through — no Next.js `/rest/next/...` route-handler hop). The old
 *   windfall-docroot proxy plumbing has been removed.
 * - `transformResponse` adapts the modern wire shapes into the camelCase
 *   view-models the dashboard components + optimistic-patch helpers consume
 *   (`SubscriptionState[]` for the dashboard, `PopularProduct[]` for the rail).
 * - Auth: a 401 is owned globally by the baseApi interceptor + the
 *   `<SessionExpiryHandler />` (see `baseApi.ts`). Components must NOT redirect
 *   to login from mutation handlers, and must not import a singleton.
 * - Optimistic updates: use `onQueryStarted` + `dispatch(autoOrderApi.util.updateQueryData(...))`
 *   with `patch.undo()` on `.catch()`. See `modifySubscriptionProduct` for the
 *   canonical pattern.
 *
 * **Type strictness:** the shapes consumed by the dashboard + product/list
 * components are typed here (`SubscriptionState`, `SubscriptionProductState`,
 * `SubscriptionAddress`, `SubscriptionPayment`).
 */

// -- Local types --

export type OrderPlacementSchedule = "weeks-2" | "months-1" | "months-2";

export type ProductActionType = "SKIP" | "PAUSE" | "RESUME" | "REMOVE" | "QUANTITY" | "FREQUENCY";

export type SubscriptionActionType = "SKIP" | "PAUSE" | "RESUME" | "REACTIVATE" | "CANCEL";

/**
 * Subscription product view-model — the per-product shape the dashboard
 * components consume, produced by `mapSubscriptions` from the modern
 * users-service `GET /users/subscriptions` response. Ported from the legacy
 * `Products.SubscriptionProduct` shape (see `legacy/.../declarations/catalog.d.ts`)
 * but typed locally (and camelCased) so we don't depend on the legacy ambient
 * namespace. Only the fields needed by ported components are required;
 * everything else stays optional or absent until a caller proves it needs them.
 */
export interface SubscriptionProductState {
  mpId: number;
  subscriptionProductId: number;
  mpName: string;
  detailLink: string;
  imagePath?: string;
  quantity: number;
  bestPrice?: number;
  isSkipped: boolean;
  isPaused: boolean;
  /** Client-only flag set by `REMOVE` optimistic updates for available
   *  products (kept in the array, rendered as "has been removed"). */
  isRemoved?: boolean;
  isAutoOrderEnabled: boolean;
  isActive: boolean;
  isForSale: boolean;
}

/**
 * Saved shipping address as it appears inside a `Subscription` envelope.
 * Only the fields the dashboard's ship-to summary line touches are
 * required; richer fields (FirstName, LastName, AddrType, etc.) will be
 * added when the edit-shipping-address page lands.
 */
export interface SubscriptionAddress {
  id?: number | string;
  companyName?: string;
  streets?: string[];
  city?: string;
  regionCd?: string;
  postalCd?: string;
}

/**
 * Saved payment method as it appears inside a `Subscription` envelope.
 * Only the fields the dashboard's bill-to summary line touches are
 * required; richer fields (Brand, ExpMonth, ExpYear, etc.) will be added
 * when the change-billing page lands.
 */
export interface SubscriptionPayment {
  /** Stripe card id of the subscription's payment method — used by Change Billing
   *  to pre-select the subscription's current card. */
  cardId?: string;
  type?: string;
  last4?: string;
}

export interface SubscriptionState {
  subscriptionId: number;
  frequency: OrderPlacementSchedule;
  products: SubscriptionProductState[];
  /** Server returns ISO date string; consumers `moment(...)` it for display. */
  nextOrderDate?: string;
  futureOrderDates?: string[];
  isSkipped: boolean;
  isPaused: boolean;
  /** Client-only flag set by the `CANCEL` optimistic patch — the endpoint
   *  returns only active subscriptions, so it's never set from the wire. */
  isRemoved?: boolean;
  payment?: SubscriptionPayment;
  shipToAddress?: SubscriptionAddress;
}

// -- users-service /users/subscriptions wire shape --
//
// These mirror SubscriptionsResponse + nested DTOs in backend/users
// (com.net32.users.model.subscription.*). `mapSubscriptions` below is the
// seam that turns this wire shape into the `SubscriptionState[]` view-model —
// converting the frequency enum to the slug the components key on and
// defaulting the availability flags the new endpoint doesn't surface.

type SubscriptionFrequency = "BI_WEEKLY" | "MONTHLY" | "BI_MONTHLY";

interface PaymentSummary {
  /** Stripe card id (PaymentSummaryDTO.cardId) — optional: older users deploys omit it. */
  cardId?: string;
  type?: string;
  last4?: string;
}

interface ShipToAddress {
  /** Address-book id (ShipToAddressDTO.addressId) — optional: older users deploys omit it. */
  addressId?: number;
  companyName?: string;
  streets?: string[];
  city?: string;
  regionCd?: string;
  postalCd?: string;
}

interface SubscriptionProduct {
  subscriptionProductId: number;
  mpId: number;
  mpName?: string;
  detailLink?: string;
  quantity: number;
  bestPrice?: number;
  isPaused: boolean;
  isSkipped: boolean;
  /** Main product image path (Neo mediaPath, e.g. /media/shared/...); rendered via ProductImage. */
  imagePath?: string;
  /** Availability triple (users service, sourced from the marketplace catalog). Optional: an
   *  older users deploy may omit them, in which case the mapper defaults to true (fail-open). */
  isAutoOrderEnabled?: boolean;
  isActive?: boolean;
  isForSale?: boolean;
}

interface Subscription {
  subscriptionId: number;
  frequency: SubscriptionFrequency;
  isPaused: boolean;
  isSkipped: boolean;
  nextOrderDate?: string;
  futureOrderDates?: string[];
  payment?: PaymentSummary;
  shipToAddress?: ShipToAddress;
  products: SubscriptionProduct[];
}

interface SubscriptionsResponse {
  subscriptions: Subscription[];
}

/**
 * Wire shape from `GET /users/subscriptions/future-dates` — the stateless
 * "what would the next dates be" projection for a brand-new subscription at
 * each frequency (used by the add-to-auto-order modal's date preview).
 */
interface FutureDatesResponse {
  schedules: { frequency: SubscriptionFrequency; dates: string[] }[];
}

/**
 * A popular auto-order product, exactly as returned by `GET /users/popular-products`
 * (clean JSON — `{ products: [...] }`, no envelope). Consumed directly by the
 * dashboard sidebar (`PopularProductCard`) and the standalone popular page.
 *
 * `productTitle` and `detailLink` are required: a product with no title or detail
 * link can never surface here per the catalog business rules, so consumers rely on
 * them being present strings.
 */
export interface PopularProduct {
  mpId: number;
  productTitle: string;
  price?: number;
  retailPrice?: number;
  detailLink: string;
  /** Main product image path (Neo mediaPath). */
  imagePath?: string;
}

interface PopularProductsResponse {
  products: PopularProduct[];
}

export interface ModifyProductArgs {
  type: ProductActionType;
  subscriptionId: number;
  subscriptionProductId: number;
  mpId: number;
  /** Required when type === "QUANTITY". */
  quantity?: number;
  /** Required when type === "QUANTITY" — for revert/analytics. */
  oldQuantity?: number;
  /** Required when type === "FREQUENCY". */
  newSubscriptionLabel?: OrderPlacementSchedule;
  /** Optional when type === "FREQUENCY" — null if destination subscription is empty. */
  newSubscriptionId?: number;
  /** Required when type === "REMOVE" — drives optimistic strategy: when the
   *  product is currently auto-orderable, the legacy keeps the row and stamps
   *  `IsRemoved = true` so a "has been removed" message can render in place;
   *  otherwise the row is spliced out. */
  productAvailable?: boolean;
}

export interface ModifySubscriptionArgs {
  type: SubscriptionActionType;
  subscriptionId: number;
}

/**
 * users-service frequency enum (BI_WEEKLY / MONTHLY / BI_MONTHLY)
 * back to the legacy slug consumers and `applyProductOptimisticPatch`
 * still match on (weeks-2 / months-1 / months-2). The legacy slug is what
 * `RecurringOrderPlacementSchedule` has always been.
 */
function frequencyEnumToLegacySlug(frequency: SubscriptionFrequency): OrderPlacementSchedule {
  switch (frequency) {
    case "BI_WEEKLY":
      return "weeks-2";
    case "MONTHLY":
      return "months-1";
    case "BI_MONTHLY":
      return "months-2";
  }
}

/**
 * Inverse of {@link frequencyEnumToLegacySlug}: the internal legacy slug the components speak
 * (weeks-2 / months-1 / months-2) → the modern users-service frequency enum the new endpoints
 * expect in request bodies (subscribe, change-frequency).
 */
function legacySlugToFrequencyEnum(slug: OrderPlacementSchedule): SubscriptionFrequency {
  switch (slug) {
    case "weeks-2":
      return "BI_WEEKLY";
    case "months-1":
      return "MONTHLY";
    case "months-2":
      return "BI_MONTHLY";
  }
}

/**
 * Map the modern wire shape from `GET /users/subscriptions` to the
 * `SubscriptionState[]` view-model the dashboard components consume. The
 * frequency enum is converted to the slug the components key on, and the three
 * availability flags the new endpoint doesn't surface are defaulted (see the
 * `getAutoOrderSubscriptions` doc for that trade-off).
 */
function mapSubscriptions(response: SubscriptionsResponse): SubscriptionState[] {
  return (response.subscriptions ?? []).map((s) => ({
    subscriptionId: s.subscriptionId,
    frequency: frequencyEnumToLegacySlug(s.frequency),
    isSkipped: s.isSkipped,
    isPaused: s.isPaused,
    // `isRemoved` is intentionally not set here — it's a client-only flag owned
    // by the CANCEL optimistic patch. The endpoint returns only active
    // subscriptions, so there's nothing to derive from the wire.
    nextOrderDate: s.nextOrderDate,
    futureOrderDates: s.futureOrderDates,
    payment: s.payment
      ? {
          cardId: s.payment.cardId,
          type: s.payment.type,
          last4: s.payment.last4,
        }
      : undefined,
    shipToAddress: s.shipToAddress
      ? {
          id: s.shipToAddress.addressId,
          companyName: s.shipToAddress.companyName,
          streets: s.shipToAddress.streets,
          city: s.shipToAddress.city,
          regionCd: s.shipToAddress.regionCd,
          postalCd: s.shipToAddress.postalCd,
        }
      : undefined,
    products: (s.products ?? []).map((p) => ({
      mpId: p.mpId,
      subscriptionProductId: p.subscriptionProductId,
      mpName: p.mpName ?? "",
      detailLink: p.detailLink ?? "",
      imagePath: p.imagePath,
      quantity: p.quantity,
      bestPrice: p.bestPrice,
      isSkipped: p.isSkipped,
      isPaused: p.isPaused,
      // Availability triple from the users service (sourced from the marketplace catalog).
      // Default true when an older users deploy omits them, so the product renders as
      // available (fail-open). The dashboard collapses these into productUnavailable.
      isAutoOrderEnabled: p.isAutoOrderEnabled ?? true,
      isActive: p.isActive ?? true,
      isForSale: p.isForSale ?? true,
    })),
  }));
}

/**
 * In-place optimistic patch for `modifySubscription`. Mutates the Immer
 * draft of `Subscriptions` to reflect what the server is about to confirm.
 * Encodes parity with the legacy `autoOrder.tsx` setState handlers, which
 * propagate the new flag down to every product inside the affected
 * subscription:
 *
 *   SKIP        — set `subscription.IsSkipped = true`; also stamp
 *                 `IsSkipped = true` on every product inside it (legacy
 *                 `handleSkipSubscriptionSuccess` does the propagation).
 *   PAUSE       — set `IsPaused = true` on the sub and every product.
 *   RESUME      — clear `IsSkipped` on the sub and every product. The
 *                 legacy keeps `IsPaused` independent — that's the
 *                 REACTIVATE action's job.
 *   REACTIVATE  — clear `IsPaused` on the sub and every product.
 *   CANCEL      — set `IsRemoved = true` on the sub. The legacy success
 *                 handler then clears `SubscriptionProducts = []`, but for
 *                 the optimistic step we leave the array in place; cache
 *                 invalidation / refetch is the authority on the empty
 *                 state once the server confirms (the product rows visibly
 *                 disappear because the list component reads `IsRemoved`
 *                 and renders nothing).
 *
 * Bail silently on a sub-id miss for the same reason `applyProductOptimisticPatch`
 * does: an out-of-band refetch may have raced ahead, and `patch.undo()`
 * will restore whatever we leave alone if the server rejects the change.
 */
function applySubscriptionOptimisticPatch(subs: SubscriptionState[], args: ModifySubscriptionArgs): void {
  const sub = subs.find((s) => s.subscriptionId === args.subscriptionId);
  if (!sub) return;
  switch (args.type) {
    case "SKIP":
      sub.isSkipped = true;
      for (const p of sub.products) p.isSkipped = true;
      return;
    case "PAUSE":
      sub.isPaused = true;
      for (const p of sub.products) p.isPaused = true;
      return;
    case "RESUME":
      sub.isSkipped = false;
      for (const p of sub.products) p.isSkipped = false;
      return;
    case "REACTIVATE":
      sub.isPaused = false;
      for (const p of sub.products) p.isPaused = false;
      return;
    case "CANCEL":
      sub.isRemoved = true;
      return;
  }
}

/**
 * In-place optimistic patch for `modifySubscriptionProduct`. Mutates the
 * Immer draft of `Subscriptions` to reflect what the server is about to
 * confirm. Encodes parity with the legacy `autoOrder.tsx` setState handlers:
 *
 *   SKIP / PAUSE     — flag the product
 *   RESUME           — clear product Skipped + Paused; also clear the parent
 *                      subscription's Paused flag (legacy did the same so a
 *                      resumed product on a paused sub un-pauses the sub for
 *                      UI purposes; the server is the source of truth on
 *                      confirm).
 *   REMOVE           — if `productAvailable`, keep the row + flip
 *                      `IsRemoved`; otherwise splice it out.
 *   QUANTITY         — overwrite `Quantity`. `oldQuantity` is carried only
 *                      for analytics; revert is via `patch.undo()`.
 *   FREQUENCY        — move the product to the TOP of the sub matching
 *                      `newSubscriptionLabel` (server returns newest first, so
 *                      the moved card lands where the refetch will confirm it).
 *                      If no subscription
 *                      at that frequency is in the cache yet (the user had none,
 *                      so the dashboard only renders a synthetic empty bucket at
 *                      render time), insert a synthetic destination here
 *                      (`subscriptionId: 0`) so the card moves immediately
 *                      instead of waiting on the post-success refetch; that
 *                      refetch (FREQUENCY's `invalidatesTags`) then swaps in the
 *                      real server-created subscription and its real id. When the
 *                      destination is empty, stub its `payment` / `shipToAddress`
 *                      from the source so the UI doesn't flicker. Inherits the
 *                      destination's `isPaused` / `isSkipped` onto the moved
 *                      product (legacy parity — a paused sub pauses anything that
 *                      lands in it).
 *
 * Bail silently on any lookup miss (an out-of-band refetch may have already
 * mutated the cache); `patch.undo()` will restore whatever state we hand
 * back if the server rejects the change.
 *
 * Exported for unit testing (the pure cache-transform is the critical logic).
 */
export function applyProductOptimisticPatch(subs: SubscriptionState[], args: ModifyProductArgs): void {
  const subIndex = subs.findIndex((s) => s.subscriptionId === args.subscriptionId);
  if (subIndex === -1) return;
  const sub = subs[subIndex];
  const productIndex = sub.products.findIndex((p) => p.subscriptionProductId === args.subscriptionProductId);

  switch (args.type) {
    case "SKIP":
      if (productIndex !== -1) sub.products[productIndex].isSkipped = true;
      return;
    case "PAUSE":
      if (productIndex !== -1) sub.products[productIndex].isPaused = true;
      return;
    case "RESUME":
      if (productIndex !== -1) {
        sub.products[productIndex].isSkipped = false;
        sub.products[productIndex].isPaused = false;
      }
      sub.isPaused = false;
      return;
    case "REMOVE":
      if (productIndex === -1) return;
      if (args.productAvailable) {
        sub.products[productIndex].isRemoved = true;
      } else {
        sub.products.splice(productIndex, 1);
      }
      return;
    case "QUANTITY":
      if (productIndex !== -1 && typeof args.quantity === "number") {
        sub.products[productIndex].quantity = args.quantity;
      }
      return;
    case "FREQUENCY": {
      if (productIndex === -1 || !args.newSubscriptionLabel) return;
      // A move to the product's current frequency is a no-op — a frequency is unique
      // per subscription, so the destination would be the source itself.
      if (args.newSubscriptionLabel === sub.frequency) return;

      let dest = subs.find((s) => s.frequency === args.newSubscriptionLabel);
      if (!dest) {
        // No subscription at the destination frequency is in the cache yet (the user
        // had none, so the dashboard only synthesizes an empty bucket for it at render
        // time). Insert a synthetic destination so the card moves immediately instead of
        // waiting on the post-success refetch. subscriptionId 0 mirrors the dashboard's
        // `emptySubscription` (falsy id -> change-shipping/billing links stay inert); the
        // FREQUENCY `invalidatesTags` refetch then replaces this stub with the real
        // server-created subscription, including its real id. Re-read it off the draft so
        // subsequent mutations are tracked by Immer.
        subs.push({
          subscriptionId: 0,
          frequency: args.newSubscriptionLabel,
          products: [],
          isSkipped: false,
          isPaused: false,
        });
        dest = subs[subs.length - 1];
      }

      const [moved] = sub.products.splice(productIndex, 1);
      if (dest.products.length === 0) {
        dest.payment = sub.payment;
        dest.shipToAddress = sub.shipToAddress;
      }
      if (dest.isPaused) moved.isPaused = true;
      if (dest.isSkipped) moved.isSkipped = true;
      // Insert at the TOP of the destination bucket to match the server, which
      // returns newest items first — so the optimistically-moved card lands in
      // the same position the post-success refetch will confirm (no visible
      // jump from bottom to top).
      dest.products.unshift(moved);
      return;
    }
  }
}

// -- Slice --

export const autoOrderApi = baseApi
  .enhanceEndpoints({
    addTagTypes: ["AUTO_ORDER_SUBSCRIPTIONS", "AUTO_ORDER_POPULAR"],
  })
  .injectEndpoints({
    endpoints: (builder) => ({
      /**
       * Dashboard load — returns all subscriptions for the current user.
       *
       * Backed by the modern users-service `GET /users/subscriptions`
       * endpoint (PI-1372 backend cutover). `transformResponse` maps the
       * wire shape into the `SubscriptionState[]` view-model the dashboard
       * components, optimistic-patch helpers, and analytics consume.
       *
       * The availability triple (`isAutoOrderEnabled`, `isActive`, `isForSale`)
       * is returned per product (the users service sources it from the marketplace
       * catalog). The mapper defaults each to `true` if a given field is absent
       * (fail-open — e.g. an older users deploy), so the "no longer for sale"
       * rendering branch lights up whenever the backend reports a product unavailable.
       */
      getAutoOrderSubscriptions: builder.query<SubscriptionState[], void>({
        query: () => ({
          url: "users/subscriptions",
          method: "GET",
        }),
        transformResponse: mapSubscriptions,
        providesTags: ["AUTO_ORDER_SUBSCRIPTIONS"],
      }),

      /**
       * Popular suggestions for the right-rail / mobile sidebar.
       * Server `limit` query param is honored; legacy front-end caps client-side at 16
       * — that hard cap should NOT be replicated; the spec calls for the new sidebar
       * to honor whatever the server returns.
       */
      getPopularAutoOrderProducts: builder.query<PopularProduct[], { limit?: number } | void>({
        query: (args) => ({
          url: "users/popular-products",
          method: "GET",
          params: args && args.limit ? { limit: args.limit } : undefined,
        }),
        transformResponse: (response: PopularProductsResponse): PopularProduct[] => response?.products ?? [],
        providesTags: ["AUTO_ORDER_POPULAR"],
      }),

      /**
       * Projected next order dates per frequency for a NEW subscription —
       * powers the add-to-auto-order modal's "Your next auto orders will be on"
       * preview. Backed by the modern stateless `GET /users/subscriptions/future-dates`.
       *
       * `transformResponse` folds the modern `{ schedules: [{ frequency, dates }] }`
       * shape into the legacy slug-keyed map (`weeks-2` / `months-1` / `months-2`
       * → ISO date strings) the modal indexes by its selected frequency. Date
       * formatting (MM/DD/YYYY) stays in the modal.
       */
      getSubscriptionFutureDates: builder.query<Record<OrderPlacementSchedule, string[]>, void>({
        query: () => ({
          url: "users/subscriptions/future-dates",
          method: "GET",
        }),
        transformResponse: (response: FutureDatesResponse): Record<OrderPlacementSchedule, string[]> => {
          const map: Record<OrderPlacementSchedule, string[]> = { "weeks-2": [], "months-1": [], "months-2": [] };
          (response?.schedules ?? []).forEach((schedule) => {
            map[frequencyEnumToLegacySlug(schedule.frequency)] = schedule.dates ?? [];
          });
          return map;
        },
      }),

      /**
       * Per-product mutation: SKIP / PAUSE / RESUME / REMOVE / QUANTITY / FREQUENCY.
       *
       * **Optimistic update pattern (canonical example — Runbook §3 rule 5).**
       * The component dispatches once and the slice handles both the optimistic
       * cache patch and the rollback on failure — replacing the legacy
       * setState-based revert logic in `autoOrder.tsx`. Components own only
       * the success-path analytics + the failure toast; the cache shape is
       * none of their business.
       *
       * The `getAutoOrderSubscriptions` cache key is `undefined` (the query
       * takes no args), so we always patch that single slot. `Subscriptions`
       * is a draft (Immer) — mutate in place.
       *
       * On a REMOVE failure or RESUME failure the legacy code re-fetched all
       * subscriptions; we don't need to, because `patch.undo()` restores the
       * exact pre-mutation cache state (which IS the server state, since the
       * cache was server-truth before our optimistic patch). Any drift would
       * be a tag-invalidation matter, not a revert one.
       *
       * `body` intentionally omits `productAvailable` — it's a client-only
       * hint for the optimistic strategy that the modern per-product endpoints
       * don't accept.
       */
      modifySubscriptionProduct: builder.mutation<unknown, ModifyProductArgs>({
        query: (args) => {
          // RESTful per-product endpoints (users service, PI-1372): one verb+path per action
          // with a JSON body, replacing the legacy single form-encoded `action-product` dispatch.
          // The component dispatch shape (ModifyProductArgs) is unchanged; the mapping lives here.
          const base = `users/subscriptions/products/${args.subscriptionProductId}`;
          switch (args.type) {
            case "SKIP":
              return { url: `${base}/skip`, method: "POST" };
            case "PAUSE":
              return { url: `${base}/pause`, method: "POST" };
            case "RESUME":
              return { url: `${base}/resume`, method: "POST" };
            case "REMOVE":
              return { url: base, method: "DELETE" };
            case "QUANTITY":
              return { url: `${base}/quantity`, method: "PUT", body: { quantity: args.quantity } };
            case "FREQUENCY":
              return {
                url: `${base}/frequency`,
                method: "PUT",
                // server finds-or-creates the target-frequency subscription; it only needs the enum.
                body: {
                  frequency: args.newSubscriptionLabel
                    ? legacySlugToFrequencyEnum(args.newSubscriptionLabel)
                    : undefined,
                },
              };
          }
        },
        async onQueryStarted(args, { dispatch, queryFulfilled }) {
          const patch = dispatch(
            autoOrderApi.util.updateQueryData("getAutoOrderSubscriptions", undefined, (draft) => {
              applyProductOptimisticPatch(draft, args);
            }),
          );
          try {
            await queryFulfilled;
          } catch {
            patch.undo();
          }
        },
        /**
         * Most mutation types (SKIP / PAUSE / RESUME / REMOVE / QUANTITY)
         * stay within a single subscription — the optimistic cache patch
         * fully reproduces server truth, so no invalidation is needed.
         *
         * FREQUENCY is different: when the destination subscription was
         * empty before the move, the server CREATES a new subscription
         * server-side with a fresh `SubscriptionId` that the optimistic
         * patch can't predict. Without a refetch, the cached destination
         * sub keeps a `SubscriptionId: undefined`, so a follow-up
         * subscription-level action would build a `users/subscriptions/undefined/...`
         * URL and fail.
         *
         * Always invalidate on FREQUENCY success. The cost is one extra
         * GET; correctness is worth it. (The legacy did the same thing —
         * see `handleProductListChangeSuccess` in autoOrder.tsx, which
         * re-fetched the subscription list whenever `newSubscriptionId`
         * had been undefined at dispatch time.)
         *
         * Other types intentionally skip invalidation to keep the
         * optimistic-only fast path.
         */
        invalidatesTags: (_result, _error, args) => (args.type === "FREQUENCY" ? ["AUTO_ORDER_SUBSCRIPTIONS"] : []),
      }),

      /**
       * Per-subscription mutation: SKIP / PAUSE / RESUME / REACTIVATE / CANCEL.
       *
       * Mirrors the same optimistic-update contract as
       * `modifySubscriptionProduct` (Runbook §3 rule 5). The component owns
       * success-path analytics + the failure toast; the cache patch +
       * rollback live here. See `applySubscriptionOptimisticPatch` for the
       * per-action shape and the rationale for propagating flags down to
       * the products inside the affected subscription.
       */
      modifySubscription: builder.mutation<unknown, ModifySubscriptionArgs>({
        query: (args) => {
          // RESTful whole-subscription endpoints (users service, PI-1372): POST per state
          // transition, DELETE for cancel — replacing the legacy form-encoded `action-subscription`.
          const base = `users/subscriptions/${args.subscriptionId}`;
          switch (args.type) {
            case "SKIP":
              return { url: `${base}/skip`, method: "POST" };
            case "PAUSE":
              return { url: `${base}/pause`, method: "POST" };
            case "RESUME":
              return { url: `${base}/resume`, method: "POST" };
            case "REACTIVATE":
              return { url: `${base}/reactivate`, method: "POST" };
            case "CANCEL":
              return { url: base, method: "DELETE" };
          }
        },
        async onQueryStarted(args, { dispatch, queryFulfilled }) {
          const patch = dispatch(
            autoOrderApi.util.updateQueryData("getAutoOrderSubscriptions", undefined, (draft) => {
              applySubscriptionOptimisticPatch(draft, args);
            }),
          );
          try {
            await queryFulfilled;
          } catch {
            patch.undo();
          }
        },
        // Note: `invalidatesTags` is intentionally omitted — see the matching
        // comment on `modifySubscriptionProduct`. CANCEL is the only case
        // where the server eventually trims `SubscriptionProducts = []`; the
        // dashboard refetches on focus / next interaction picks that up.
      }),

      /**
       * Point the subscription at an existing shipping address the user owns
       * (users service `PUT /subscriptions/{id}/shipping-address`, PI-1372). The
       * legacy single "create+assign" endpoint is split client-side: the Change
       * Shipping pages first create/update the address via `userApi`, then chain
       * this assign with the resulting address id.
       *
       * No optimistic update (deliberate departure from the modify* pattern):
       * on success the page immediately navigates back to the dashboard, so
       * there is no in-place UI to patch — the `AUTO_ORDER_SUBSCRIPTIONS`
       * invalidation refetch is the authority for the ship-to summary line.
       * 401 is owned by the baseApi interceptor; 404/422/500 surface as a
       * toast in the calling page.
       */
      assignSubscriptionShippingAddress: builder.mutation<unknown, { subscriptionId: number; addressId: number }>({
        query: ({ subscriptionId, addressId }) => ({
          url: `users/subscriptions/${subscriptionId}/shipping-address`,
          method: "PUT",
          body: { addressId },
        }),
        invalidatesTags: ["AUTO_ORDER_SUBSCRIPTIONS"],
      }),

      /**
       * Point the subscription at an existing saved card (Stripe cardId) the
       * user owns (users service `PUT /subscriptions/{id}/payment-method`,
       * PI-1372). For a new card, the Change Billing page first tokenizes +
       * saves it via the shared BillingForm (`userApi.addCreditCard`), then
       * chains this assign with the fresh cardId. Same no-optimistic-update /
       * invalidate-on-success rationale as the shipping assign above.
       */
      assignSubscriptionPaymentMethod: builder.mutation<unknown, { subscriptionId: number; cardId: string }>({
        query: ({ subscriptionId, cardId }) => ({
          url: `users/subscriptions/${subscriptionId}/payment-method`,
          method: "PUT",
          body: { cardId },
        }),
        invalidatesTags: ["AUTO_ORDER_SUBSCRIPTIONS"],
      }),
    }),
  });

export const {
  useGetAutoOrderSubscriptionsQuery,
  useGetPopularAutoOrderProductsQuery,
  useGetSubscriptionFutureDatesQuery,
  useModifySubscriptionProductMutation,
  useModifySubscriptionMutation,
  useAssignSubscriptionShippingAddressMutation,
  useAssignSubscriptionPaymentMethodMutation,
} = autoOrderApi;
